import { useState, useEffect, useCallback, useMemo, FormEvent } from 'react'
import { motion } from 'framer-motion'
import {
  Search,
  RefreshCw,
  AlertCircle,
  ShieldCheck,
  UserPlus,
  Trash2,
  Lock,
  X,
  CheckCircle2,
  Mail,
  Phone,
  Calendar,
  KeyRound,
} from 'lucide-react'
import toast from 'react-hot-toast'
import {
  adminUsersService,
  AdminUser,
  AdminPermissionDef,
} from '../services/adminUsersService'
import {
  ADMIN_PERMISSIONS as STATIC_PERMISSIONS,
  DEFAULT_SUPPORT_ADMIN_PERMISSIONS,
} from '../constants/permissions'
import { useAuth } from '../contexts/AuthContext'
import ConfirmationDialog from '../components/ConfirmationDialog'

type Tab = 'list' | 'create'

interface CreateFormState {
  firstName: string
  lastName: string
  email: string
  phone: string
  password: string
  confirmPassword: string
  permissions: string[]
}

const emptyForm: CreateFormState = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  password: '',
  confirmPassword: '',
  permissions: [...DEFAULT_SUPPORT_ADMIN_PERMISSIONS],
}

const AdminUsers = () => {
  const { user: currentUser } = useAuth()

  const [tab, setTab] = useState<Tab>('list')
  const [admins, setAdmins] = useState<AdminUser[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [actionId, setActionId] = useState<string | null>(null)

  const [permissionCatalog, setPermissionCatalog] =
    useState<AdminPermissionDef[]>(STATIC_PERMISSIONS)

  const [form, setForm] = useState<CreateFormState>(emptyForm)
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)

  const [editing, setEditing] = useState<AdminUser | null>(null)
  const [editingPermissions, setEditingPermissions] = useState<string[]>([])
  const [savingPermissions, setSavingPermissions] = useState(false)

  const [confirmDelete, setConfirmDelete] = useState<AdminUser | null>(null)

  const grouped = useMemo(() => {
    return permissionCatalog.reduce<Record<string, AdminPermissionDef[]>>((acc, p) => {
      if (!acc[p.group]) acc[p.group] = []
      acc[p.group].push(p)
      return acc
    }, {})
  }, [permissionCatalog])

  const fetchAdmins = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const list = await adminUsersService.listAdmins({
        search: search.trim() || undefined,
      })
      setAdmins(list)
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load admins')
    } finally {
      setLoading(false)
    }
  }, [search])

  const fetchPermissions = useCallback(async () => {
    try {
      const data = await adminUsersService.listPermissions()
      if (Array.isArray(data.permissions) && data.permissions.length > 0) {
        setPermissionCatalog(data.permissions)
      }
    } catch {
      // Fallback to static catalog (already set in initial state)
    }
  }, [])

  useEffect(() => {
    fetchPermissions()
  }, [fetchPermissions])

  useEffect(() => {
    fetchAdmins()
  }, [fetchAdmins])

  const togglePermission = (
    setter: (next: string[]) => void,
    current: string[],
    key: string,
  ) => {
    if (current.includes(key)) {
      setter(current.filter((k) => k !== key))
    } else {
      setter([...current, key])
    }
  }

  const validateCreate = (): boolean => {
    const next: Record<string, string> = {}
    if (!form.firstName.trim() || form.firstName.trim().length < 2) {
      next.firstName = 'First name must be at least 2 characters'
    }
    if (!form.email.trim() || !/^\S+@\S+\.\S+$/.test(form.email.trim())) {
      next.email = 'Enter a valid email address'
    }
    if (!form.password || form.password.length < 8) {
      next.password = 'Password must be at least 8 characters'
    }
    if (form.confirmPassword !== form.password) {
      next.confirmPassword = 'Passwords do not match'
    }
    if (form.phone && !/^\+?[\d\s\-()]{6,20}$/.test(form.phone.trim())) {
      next.phone = 'Enter a valid phone number'
    }
    if (form.permissions.length === 0) {
      next.permissions = 'Select at least one permission'
    }
    setFormErrors(next)
    return Object.keys(next).length === 0
  }

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault()
    if (!validateCreate()) {
      toast.error('Please fix the errors before creating the admin')
      return
    }
    setSubmitting(true)
    try {
      const created = await adminUsersService.createAdmin({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim() || undefined,
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim() || undefined,
        password: form.password,
        role: 'SUPPORT_ADMIN',
        permissions: form.permissions,
      })
      toast.success(`Support admin ${created.email} created`)
      setForm(emptyForm)
      setFormErrors({})
      setTab('list')
      fetchAdmins()
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to create admin')
    } finally {
      setSubmitting(false)
    }
  }

  const handleSavePermissions = async () => {
    if (!editing) return
    setSavingPermissions(true)
    try {
      const updated = await adminUsersService.updatePermissions(
        editing._id,
        editingPermissions,
      )
      toast.success('Permissions updated')
      setAdmins((prev) => prev.map((a) => (a._id === updated._id ? updated : a)))
      setEditing(null)
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to update permissions')
    } finally {
      setSavingPermissions(false)
    }
  }

  const handleToggleStatus = async (admin: AdminUser) => {
    if (admin._id === currentUser?._id) {
      toast.error('You cannot change your own status here')
      return
    }
    setActionId(admin._id)
    try {
      const updated = await adminUsersService.toggleStatus(admin._id, !admin.isActive)
      setAdmins((prev) => prev.map((a) => (a._id === updated._id ? updated : a)))
      toast.success(updated.isActive ? 'Admin activated' : 'Admin deactivated')
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to update status')
    } finally {
      setActionId(null)
    }
  }

  const handleDelete = async () => {
    if (!confirmDelete) return
    setActionId(confirmDelete._id)
    try {
      await adminUsersService.deleteAdmin(confirmDelete._id)
      setAdmins((prev) => prev.filter((a) => a._id !== confirmDelete._id))
      toast.success('Admin deleted')
      setConfirmDelete(null)
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to delete admin')
    } finally {
      setActionId(null)
    }
  }

  if (currentUser && currentUser.role !== 'SUPER_ADMIN') {
    return (
      <div className="max-w-3xl mx-auto mt-12">
        <div className="bg-white border border-red-200 rounded-xl shadow-sm p-6 flex items-start gap-3">
          <Lock className="w-5 h-5 text-red-500 mt-0.5" />
          <div>
            <h2 className="text-lg font-semibold text-red-700">Super admin only</h2>
            <p className="text-sm text-red-600 mt-1">
              Only super admins can view or manage admin users and permissions.
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-ivory-900 flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-amber-600" /> Admin Users &amp; Permissions
          </h1>
          <p className="text-sm text-ivory-600 mt-1">
            Create support admins and control exactly which areas of the dashboard they can access.
          </p>
        </div>
        <div className="inline-flex bg-ivory-100 rounded-lg p-1 self-start">
          <button
            type="button"
            onClick={() => setTab('list')}
            className={`px-4 py-1.5 text-sm font-semibold rounded-md transition ${
              tab === 'list' ? 'bg-white shadow text-ivory-900' : 'text-ivory-600'
            }`}
          >
            Admins
          </button>
          <button
            type="button"
            onClick={() => setTab('create')}
            className={`px-4 py-1.5 text-sm font-semibold rounded-md transition flex items-center gap-1 ${
              tab === 'create' ? 'bg-white shadow text-ivory-900' : 'text-ivory-600'
            }`}
          >
            <UserPlus className="w-4 h-4" /> New support admin
          </button>
        </div>
      </div>

      {tab === 'list' && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-ivory-200 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ivory-400" />
              <input
                type="text"
                placeholder="Search admins by name or email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 pr-3 py-2 w-full border border-ivory-200 rounded-md focus:ring-2 focus:ring-amber-500/30 focus:border-amber-500 text-sm"
              />
            </div>
            <button
              type="button"
              onClick={fetchAdmins}
              className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-ivory-700 bg-ivory-100 hover:bg-ivory-200 rounded-md transition"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </button>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
              <div className="text-sm text-red-700">{error}</div>
            </div>
          )}

          <div className="bg-white rounded-xl border border-ivory-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-ivory-200">
                <thead className="bg-ivory-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-ivory-600 uppercase">Admin</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-ivory-600 uppercase">Role</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-ivory-600 uppercase">Permissions</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-ivory-600 uppercase">Status</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-ivory-600 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-ivory-100">
                  {loading && admins.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-sm text-ivory-500">
                        <RefreshCw className="w-5 h-5 animate-spin inline-block mr-2" /> Loading admins...
                      </td>
                    </tr>
                  )}
                  {!loading && admins.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-sm text-ivory-500">
                        No admins found. Create one with the "New support admin" tab.
                      </td>
                    </tr>
                  )}
                  {admins.map((admin) => {
                    const isSelf = admin._id === currentUser?._id
                    const isSuper = admin.role === 'SUPER_ADMIN'
                    return (
                      <tr key={admin._id} className="hover:bg-ivory-50/50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-amber-400 to-amber-600 text-white flex items-center justify-center font-semibold text-sm">
                              {(admin.firstName?.[0] ?? '?').toUpperCase()}
                              {(admin.lastName?.[0] ?? '').toUpperCase()}
                            </div>
                            <div className="min-w-0">
                              <div className="text-sm font-semibold text-ivory-900 truncate">
                                {admin.firstName} {admin.lastName} {isSelf && <span className="text-xs text-amber-600">(you)</span>}
                              </div>
                              <div className="text-xs text-ivory-500 truncate">{admin.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded ${
                            isSuper ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                          }`}>
                            {isSuper ? 'Super admin' : 'Support admin'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {isSuper ? (
                            <span className="text-xs text-ivory-500 italic">All permissions</span>
                          ) : admin.permissions.length === 0 ? (
                            <span className="text-xs text-red-600">No permissions assigned</span>
                          ) : (
                            <div className="flex flex-wrap gap-1 max-w-md">
                              {admin.permissions.slice(0, 4).map((key) => {
                                const def = permissionCatalog.find((p) => p.key === key)
                                return (
                                  <span
                                    key={key}
                                    className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] bg-ivory-100 text-ivory-700 rounded"
                                  >
                                    <KeyRound className="w-3 h-3" /> {def?.label ?? key}
                                  </span>
                                )
                              })}
                              {admin.permissions.length > 4 && (
                                <span className="text-[11px] text-ivory-500">
                                  +{admin.permissions.length - 4} more
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded ${
                            admin.isActive ? 'bg-green-100 text-green-700' : 'bg-ivory-200 text-ivory-600'
                          }`}>
                            {admin.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-2">
                            {!isSuper && (
                              <button
                                type="button"
                                onClick={() => {
                                  setEditing(admin)
                                  setEditingPermissions([...admin.permissions])
                                }}
                                className="text-xs font-medium px-3 py-1.5 bg-amber-50 text-amber-700 hover:bg-amber-100 rounded-md transition"
                              >
                                Edit permissions
                              </button>
                            )}
                            <button
                              type="button"
                              disabled={isSelf || actionId === admin._id}
                              onClick={() => handleToggleStatus(admin)}
                              className={`text-xs font-medium px-3 py-1.5 rounded-md transition ${
                                admin.isActive
                                  ? 'bg-ivory-100 text-ivory-700 hover:bg-ivory-200'
                                  : 'bg-green-50 text-green-700 hover:bg-green-100'
                              } disabled:opacity-50 disabled:cursor-not-allowed`}
                            >
                              {admin.isActive ? 'Deactivate' : 'Activate'}
                            </button>
                            {!isSelf && !isSuper && (
                              <button
                                type="button"
                                onClick={() => setConfirmDelete(admin)}
                                disabled={actionId === admin._id}
                                className="p-1.5 text-red-600 hover:bg-red-50 rounded-md transition disabled:opacity-50"
                                title="Delete admin"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {tab === 'create' && (
        <motion.form
          onSubmit={handleCreate}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white border border-ivory-200 rounded-xl shadow-sm p-6 space-y-6"
        >
          <div>
            <h2 className="text-lg font-semibold text-ivory-900">New support admin</h2>
            <p className="text-sm text-ivory-600 mt-1">
              Create a teammate account for the BookMyYogaTTC admin dashboard. You can grant
              and revoke permissions any time after creation.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field
              label="First name"
              error={formErrors.firstName}
              icon={<UserPlus className="w-4 h-4" />}
            >
              <input
                type="text"
                value={form.firstName}
                onChange={(e) => setForm((s) => ({ ...s, firstName: e.target.value }))}
                className="w-full px-3 py-2 border border-ivory-300 rounded-md text-sm focus:ring-2 focus:ring-amber-500/30 focus:border-amber-500"
                placeholder="Jane"
              />
            </Field>
            <Field label="Last name" error={formErrors.lastName}>
              <input
                type="text"
                value={form.lastName}
                onChange={(e) => setForm((s) => ({ ...s, lastName: e.target.value }))}
                className="w-full px-3 py-2 border border-ivory-300 rounded-md text-sm focus:ring-2 focus:ring-amber-500/30 focus:border-amber-500"
                placeholder="Doe"
              />
            </Field>
            <Field
              label="Email"
              error={formErrors.email}
              icon={<Mail className="w-4 h-4" />}
            >
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))}
                className="w-full px-3 py-2 border border-ivory-300 rounded-md text-sm focus:ring-2 focus:ring-amber-500/30 focus:border-amber-500"
                placeholder="jane@bookmyyogattc.com"
              />
            </Field>
            <Field
              label="Phone (optional)"
              error={formErrors.phone}
              icon={<Phone className="w-4 h-4" />}
            >
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => setForm((s) => ({ ...s, phone: e.target.value }))}
                className="w-full px-3 py-2 border border-ivory-300 rounded-md text-sm focus:ring-2 focus:ring-amber-500/30 focus:border-amber-500"
                placeholder="+91 90000 12345"
              />
            </Field>
            <Field
              label="Password"
              error={formErrors.password}
              icon={<Lock className="w-4 h-4" />}
            >
              <input
                type="password"
                value={form.password}
                onChange={(e) => setForm((s) => ({ ...s, password: e.target.value }))}
                className="w-full px-3 py-2 border border-ivory-300 rounded-md text-sm focus:ring-2 focus:ring-amber-500/30 focus:border-amber-500"
                placeholder="Minimum 8 characters"
                autoComplete="new-password"
              />
            </Field>
            <Field
              label="Confirm password"
              error={formErrors.confirmPassword}
              icon={<Lock className="w-4 h-4" />}
            >
              <input
                type="password"
                value={form.confirmPassword}
                onChange={(e) =>
                  setForm((s) => ({ ...s, confirmPassword: e.target.value }))
                }
                className="w-full px-3 py-2 border border-ivory-300 rounded-md text-sm focus:ring-2 focus:ring-amber-500/30 focus:border-amber-500"
                placeholder="Re-enter password"
                autoComplete="new-password"
              />
            </Field>
          </div>

          <div className="border-t border-ivory-200 pt-4">
            <div className="flex items-start justify-between gap-4 mb-3">
              <div>
                <h3 className="text-sm font-semibold text-ivory-900">Permissions</h3>
                <p className="text-xs text-ivory-500 mt-1">
                  Pick exactly what this support admin is allowed to do. Defaults are pre-selected
                  for the typical school onboarding workflow.
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setForm((s) => ({ ...s, permissions: [...DEFAULT_SUPPORT_ADMIN_PERMISSIONS] }))}
                  className="text-xs px-2 py-1 bg-ivory-100 hover:bg-ivory-200 text-ivory-700 rounded"
                >
                  Reset to defaults
                </button>
                <button
                  type="button"
                  onClick={() => setForm((s) => ({ ...s, permissions: [] }))}
                  className="text-xs px-2 py-1 bg-ivory-100 hover:bg-ivory-200 text-ivory-700 rounded"
                >
                  Clear all
                </button>
              </div>
            </div>
            {formErrors.permissions && (
              <p className="text-xs text-red-600 mb-2">{formErrors.permissions}</p>
            )}
            <PermissionPicker
              grouped={grouped}
              selected={form.permissions}
              onToggle={(key) =>
                togglePermission(
                  (next) => setForm((s) => ({ ...s, permissions: next })),
                  form.permissions,
                  key,
                )
              }
            />
          </div>

          <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-3 pt-2 border-t border-ivory-100">
            <button
              type="button"
              onClick={() => {
                setForm(emptyForm)
                setFormErrors({})
                setTab('list')
              }}
              className="px-4 py-2 text-sm font-medium text-ivory-700 bg-ivory-100 hover:bg-ivory-200 rounded-md"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-5 py-2 text-sm font-semibold text-white bg-amber-600 hover:bg-amber-700 rounded-md disabled:opacity-60 inline-flex items-center gap-2"
            >
              {submitting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              {submitting ? 'Creating...' : 'Create support admin'}
            </button>
          </div>
        </motion.form>
      )}

      {/* Edit permissions modal */}
      {editing && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-ivory-900/40 backdrop-blur-sm p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-2xl border border-ivory-200 max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col"
          >
            <div className="flex items-start justify-between p-5 border-b border-ivory-200">
              <div>
                <h2 className="text-lg font-semibold text-ivory-900">
                  Edit permissions
                </h2>
                <p className="text-sm text-ivory-500 mt-0.5">
                  {editing.firstName} {editing.lastName} &middot; {editing.email}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="p-1.5 hover:bg-ivory-100 rounded"
              >
                <X className="w-5 h-5 text-ivory-500" />
              </button>
            </div>
            <div className="p-5 overflow-y-auto">
              <PermissionPicker
                grouped={grouped}
                selected={editingPermissions}
                onToggle={(key) =>
                  togglePermission(setEditingPermissions, editingPermissions, key)
                }
              />
              <p className="mt-4 text-xs text-ivory-500">
                <Calendar className="w-3 h-3 inline-block mr-1" />
                Last login: {editing.lastLogin ? new Date(editing.lastLogin).toLocaleString() : 'Never'}
              </p>
            </div>
            <div className="flex items-center justify-end gap-3 p-4 border-t border-ivory-200 bg-ivory-50">
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="px-4 py-2 text-sm font-medium text-ivory-700 bg-white border border-ivory-200 hover:bg-ivory-100 rounded-md"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSavePermissions}
                disabled={savingPermissions}
                className="px-5 py-2 text-sm font-semibold text-white bg-amber-600 hover:bg-amber-700 rounded-md disabled:opacity-60 inline-flex items-center gap-2"
              >
                {savingPermissions ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <CheckCircle2 className="w-4 h-4" />
                )}
                Save permissions
              </button>
            </div>
          </motion.div>
        </div>
      )}

      <ConfirmationDialog
        isOpen={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={handleDelete}
        title="Delete admin"
        message={`Permanently delete ${confirmDelete?.firstName ?? ''} ${confirmDelete?.lastName ?? ''}?`}
        details={confirmDelete?.email}
        confirmText="Delete"
        type="danger"
        isLoading={actionId === confirmDelete?._id}
      />
    </div>
  )
}

interface FieldProps {
  label: string
  error?: string
  icon?: React.ReactNode
  children: React.ReactNode
}

const Field = ({ label, error, icon, children }: FieldProps) => (
  <label className="block">
    <span className="text-xs font-semibold text-ivory-700 uppercase tracking-wide flex items-center gap-1 mb-1">
      {icon}
      {label}
    </span>
    {children}
    {error && <span className="text-xs text-red-600 mt-1 block">{error}</span>}
  </label>
)

interface PermissionPickerProps {
  grouped: Record<string, AdminPermissionDef[]>
  selected: string[]
  onToggle: (key: string) => void
}

const PermissionPicker = ({ grouped, selected, onToggle }: PermissionPickerProps) => (
  <div className="space-y-4">
    {Object.entries(grouped).map(([group, perms]) => (
      <div key={group} className="border border-ivory-200 rounded-lg overflow-hidden">
        <div className="px-3 py-2 bg-ivory-50 border-b border-ivory-200 text-xs font-semibold uppercase tracking-wide text-ivory-600">
          {group}
        </div>
        <div className="divide-y divide-ivory-100">
          {perms.map((perm) => {
            const checked = selected.includes(perm.key)
            return (
              <label
                key={perm.key}
                className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition ${
                  checked ? 'bg-amber-50/40' : 'hover:bg-ivory-50'
                }`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => onToggle(perm.key)}
                  className="w-4 h-4 text-amber-600 rounded border-ivory-300 focus:ring-amber-500"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-ivory-800">{perm.label}</div>
                  <div className="text-[11px] text-ivory-500 font-mono">{perm.key}</div>
                </div>
              </label>
            )
          })}
        </div>
      </div>
    ))}
  </div>
)

export default AdminUsers
